/** A régua de silêncio — elemento-assinatura do produto (DESIGN.md § 1.6).
 *
 * Faixa de 3px dividida em 14 segmentos, um por dia. Dia com interação acende
 * em petróleo; dia em silêncio fica em névoa. Silêncio prolongado no trecho
 * final vira âmbar.
 *
 * Substitui o rótulo "há 12 dias sem contato": o padrão é lido antes do texto,
 * cabe em 320px, não ocupa linha e codifica dado real. É o único elemento
 * visualmente ousado do sistema — tudo ao redor permanece contido.
 */

const DIAS = 14;

/** A partir de quantos dias finais em silêncio a régua vira âmbar.
 * Alinhado com `is_stale` do read model — a régua e o card contam a mesma
 * história, e divergir aqui faria o usuário desconfiar dos dois. */
const LIMITE_DE_SILENCIO = 7;

interface Props {
  /** Um item por dia, do mais antigo ao mais recente. Menos de 14 dias — uma
   * oportunidade recém-criada — preenche o início com silêncio. */
  diasComSinal: readonly boolean[];
  /** Descrição para leitor de tela. A régua é visual; o texto é o mesmo dado. */
  descricao?: string;
}

export function ReguaDeSilencio({ diasComSinal, descricao }: Props) {
  const dias = normalizar(diasComSinal);
  const silencioFinal = contarSilencioFinal(dias);
  const esfriou = silencioFinal >= LIMITE_DE_SILENCIO;

  const texto =
    descricao ??
    (silencioFinal === 0
      ? "Interação hoje."
      : `${silencioFinal} ${silencioFinal === 1 ? "dia" : "dias"} sem interação.`);

  return (
    <div
      role="img"
      aria-label={texto}
      title={texto}
      className="flex h-[3px] w-full gap-px overflow-hidden rounded-t-md"
    >
      {dias.map((temSinal, indice) => {
        // Só o trecho final em silêncio prolongado vira âmbar. Um silêncio
        // antigo, já rompido, é histórico — não pede ação hoje.
        const noTrechoFinal = indice >= DIAS - silencioFinal;
        const cor = temSinal ? "bg-acao" : esfriou && noTrechoFinal ? "bg-ambar" : "bg-borda";

        return <span key={indice} aria-hidden="true" className={`h-full flex-1 ${cor}`} />;
      })}
    </div>
  );
}

/** Ajusta para exatamente 14 dias: corta o excesso antigo, completa o começo
 * com silêncio quando há menos histórico que isso. */
function normalizar(dias: readonly boolean[]): boolean[] {
  if (dias.length >= DIAS) return [...dias.slice(dias.length - DIAS)];
  return [...Array<boolean>(DIAS - dias.length).fill(false), ...dias];
}

function contarSilencioFinal(dias: readonly boolean[]): number {
  let total = 0;
  for (let i = dias.length - 1; i >= 0 && dias[i] === false; i--) total++;
  return total;
}
